import {
  AxiosError,
  AxiosResponse,
} from 'axios';
import {
  acceptHMRUpdate, defineStore,
} from 'pinia';

import {
  asyncRequestAction,
  asyncRequestActionList,
} from './util/request';

import {
  TFindDataSourcesRouteResponse,
  TGetAssetTreeResponse,
  TGetDataSourcesForAssetResponse,
  TGetEquipmentStatusResponse,
  findDataSourcesRoute,
  getAssetTree,
  getDataSourcesForAsset,
  getEquipmentStatus,
} from '@/generated/qmulus/axios/core.ts';
import {
  EHierarchy, TAssetOverview, TFlattenedAssetTreeNode,
} from '@/types/asset-tree.ts';
import {
  TComponentValue,
} from '@/types/openapi-type.ts';
import {
  excludeOfflineAssets,
  flattenAssetTree,
} from '@/utils/asset-tree.ts';
import {
  EMPTY_ARRAY, isNullish,
} from '@/utils/common';
import {
  globalConcurrencyManager,
} from '@/utils/concurrency.ts';
import {
  type TWorkplaceFilter,
  getAssetAssociationListByWorkplaceFilter,
} from '@/utils/data-sources';

type TAssetId = number;
type TDataSourceType = TComponentValue<'DataSourceType'>;

function buildAssetOverviewMap(flattenedAssetTree: TFlattenedAssetTreeNode[]): Record<TAssetId, TAssetOverview> {
  const tempMap: Record<TAssetId, TAssetOverview> = {};
  flattenedAssetTree.forEach((node) => {
    const ancestorMap = new Map<EHierarchy, number>();
    node.path.forEach((ancestor) => {
      ancestorMap.set(ancestor.hierarchyLevel as EHierarchy, ancestor.assetId);
    });
    tempMap[node.assetId] = {
      ...node,
      ancestors: ancestorMap,
    };
  });
  return tempMap;
}

export const useAssetStore = defineStore('asset', {
  state: () => ({
    assetDataSourceListMap: {} as Record<TAssetId, TGetDataSourcesForAssetResponse | undefined>,
    assetDataSourceListPromiseMap: {} as Record<TAssetId, Promise<AxiosResponse<TGetDataSourcesForAssetResponse>> | undefined>,
    assetTreeWithOfflineAssets: undefined as TGetAssetTreeResponse | undefined,
    assetTreeMapWithOfflineAssets: {} as Record<EHierarchy, TGetAssetTreeResponse>,
    assetTreePromiseMap: {} as Record<EHierarchy, Promise<AxiosResponse<TGetAssetTreeResponse>>>,
    assetIdSortOrder: [] as number[],
    assetOverviewMap: undefined as Record<TAssetId, TAssetOverview> | undefined,
    assetOverviewMapWithOfflineAssets: undefined as Record<TAssetId, TAssetOverview> | undefined,
    assetOverviewPromise: undefined as Promise<AxiosResponse<TGetAssetTreeResponse>> | undefined,
    currentAssetId: undefined as number | undefined,
    dataSourceMap: {} as Record<number, TFindDataSourcesRouteResponse[number]>,
    dataSourceList: undefined as TFindDataSourcesRouteResponse | undefined,
    dataSourceListPromise: undefined as Promise<AxiosResponse<TFindDataSourcesRouteResponse>> | undefined,
    equipmentStatusMap: {} as Record<TAssetId, TGetEquipmentStatusResponse>,
    equipmentStatusTimestampMap: {} as Record<TAssetId, number>,
    equipmentStatusPromiseMap: {} as Record<TAssetId, Promise<TGetEquipmentStatusResponse>>,
  }),
  getters: {
    assetDataSourceListIsLoading: (state) => (assetId: TAssetId) => state.assetDataSourceListPromiseMap[assetId] !== undefined,
    isAssetTreeLoading: (state) => (maxIncludedHierarchyLevel: EHierarchy) => state.assetTreePromiseMap[maxIncludedHierarchyLevel] !== undefined,
    assetTree: (state): TGetAssetTreeResponse | undefined => (state.assetTreeWithOfflineAssets
      ? excludeOfflineAssets(state.assetTreeWithOfflineAssets)
      : undefined),
    assetTreeMap: (state): Record<EHierarchy, TGetAssetTreeResponse> => {
      const assetTreeEntryList = Object.entries(state.assetTreeMapWithOfflineAssets).map(([
        maxIncludedHierarchyLevel,
        assetTree,
      ]): [string, TGetAssetTreeResponse] => [
        maxIncludedHierarchyLevel,
        excludeOfflineAssets(assetTree),
      ]);
      return Object.fromEntries(assetTreeEntryList) as Record<EHierarchy, TGetAssetTreeResponse>;
    },
    assetOverview: (state) => (assetId: TAssetId): TAssetOverview | undefined => (state.assetOverviewMap ? state.assetOverviewMap[assetId] : undefined),
    isMultiChamber: (state) => (assetId: TAssetId): boolean | undefined => {
      const assetDetails = state.assetOverviewMap ? state.assetOverviewMap[assetId] : undefined;
      if (isNullish(assetDetails) || isNullish(assetDetails.children)) {
        return undefined;
      }
      const childrenChamberCount = assetDetails.children.filter((child) => child.hierarchyLevel === EHierarchy.CHAMBER).length;
      return childrenChamberCount > 1;
    },
    isAssetOverviewLoading: (state) => state.assetOverviewPromise !== undefined,
    isDataSourceListLoading: (state) => state.dataSourceListPromise !== undefined,
    isEquipmentStatusLoading: (state) => (assetId: TAssetId) => state.equipmentStatusPromiseMap[assetId] !== undefined,
    equipmentStatus: (state) => (assetId: TAssetId): TGetEquipmentStatusResponse | undefined => state.equipmentStatusMap[assetId],
    getAssetAssociationListByWorkplaceFilter: (state) => (filter: TWorkplaceFilter) => state.dataSourceList
      ? getAssetAssociationListByWorkplaceFilter(state.dataSourceList, filter)
      : EMPTY_ARRAY,
  },
  actions: {
    async loadAssetDataSourceList(assetId: TAssetId) {
      return asyncRequestAction({
        id: assetId,
        callback: () => getDataSourcesForAsset({
          path: {
            assetId,
          },
        }),
        record: this.assetDataSourceListMap,
        promiseMap: this.assetDataSourceListPromiseMap,
      });
    },
    async loadDataSourceList(force?: boolean) {
      return asyncRequestActionList({
        callback: () => findDataSourcesRoute({}),
        list: this.dataSourceList,
        promise: this.dataSourceListPromise,
        setList: (data) => {
          this.dataSourceList = data;
          this.dataSourceMap = data.reduce(
            (acc, item) => {
              acc[item.dataSourceId] = item;
              return acc;
            },
            {} as Record<number, TFindDataSourcesRouteResponse[number]>,
          );
        },
        setPromise: (promise) => {
          this.dataSourceListPromise = promise;
        },
        force,
      });
    },
    async loadAssetDataSourceAssociation(assetId: TAssetId, dataSourceType: TDataSourceType): Promise<TComponentValue<'DataSourceAssociation'> | undefined> {
      let dataSources: TGetDataSourcesForAssetResponse;
      try {
        dataSources = await this.loadAssetDataSourceList(assetId);
      } catch (e) {
        if ((e as AxiosError).status === 404) {
          return undefined;
        }
        throw e;
      }
      return dataSources.find((dataSource) => dataSource.dataSourceType === dataSourceType)?.assetAssociations
        .filter((assetAssociation) => assetAssociation.assetId === assetId)[0];
    },
    async loadAssetTree(maxIncludedHierarchyLevel: EHierarchy, force?: boolean): Promise<TGetAssetTreeResponse> {
      await asyncRequestAction({
        id: maxIncludedHierarchyLevel,
        callback: () => getAssetTree({
          query: {
            maxIncludedHierarchyLevel,
            excludeOfflineTypes: false,
          },
        }),
        record: this.assetTreeMapWithOfflineAssets,
        promiseMap: this.assetTreePromiseMap,
        force,
      });
      return this.assetTreeMap[maxIncludedHierarchyLevel];
    },
    async loadAssetOverview(force?: boolean): Promise<TGetAssetTreeResponse> {
      await asyncRequestActionList({
        callback: () => getAssetTree({
          query: {
            maxIncludedHierarchyLevel: EHierarchy.COMPONENT,
            omitRetiredAssets: false,
            excludeOfflineTypes: false,
          },
        }),
        list: this.assetTreeWithOfflineAssets,
        promise: this.assetOverviewPromise,
        setList: (data) => {
          this.assetTreeWithOfflineAssets = data;

          const flattenedAssetTree = flattenAssetTree(excludeOfflineAssets(data));
          this.assetIdSortOrder = flattenedAssetTree.map((assetNode) => assetNode.assetId);
          this.assetOverviewMap = buildAssetOverviewMap(flattenedAssetTree);
          this.assetOverviewMapWithOfflineAssets = buildAssetOverviewMap(flattenAssetTree(data));
        },
        setPromise: (promise) => {
          this.assetOverviewPromise = promise;
        },
        force,
      });
      return this.assetTree!;
    },
    /*
     * Return true if the assetId is the same, a descendant or an ancestor of the
     * referenceAssetId.
     */
    isInPathOf(assetId: number, referenceAssetId: number): boolean {
      return assetId === referenceAssetId
        || this.isDescendantOf(assetId, referenceAssetId)
        || this.isAncestorOf(assetId, referenceAssetId);
    },
    /* Return true of the assetId is a descendant (child) of the referenceAssetId */
    isDescendantOf(assetId: number, referenceAssetId: number): boolean {
      const assetParentId = this.assetOverview(assetId)?.parentId;

      // 0 (ROOT_NODE) is also a valid stop condition
      if (!assetParentId) {
        return false;
      }
      return assetParentId === referenceAssetId
        || this.isDescendantOf(assetParentId, referenceAssetId);
    },
    /* Return true of the assetId is an ancestor (parent) of the referenceAssetId */
    isAncestorOf(assetId: number, referenceAssetId: number): boolean {
      // It is more efficient to walk up the tree than to walk down the tree.
      // Thus switching the inputs and check for descendants is better than looping
      // over all children.
      return this.isDescendantOf(referenceAssetId, assetId);
    },
    /*
     * Return the relative path to the assetId from the referenceAssetId.
     * Returns the full path if the reference is undefined or the assetId is not a descendant of the reference.
     */
    relativePathTo(assetId: number, referenceAssetId?: number): TFlattenedAssetTreeNode[] {
      if (assetId === referenceAssetId) {
        return [];
      }
      const assetNode = this.assetOverviewMapWithOfflineAssets?.[assetId];
      if (!assetNode) {
        return [];
      }

      const fullPath = assetNode.path;
      const referenceIndex = fullPath.findIndex((node) => node.assetId === referenceAssetId);

      if (referenceIndex === -1) {
        return fullPath;
      }

      // +1 to include the referenceAssetId which harmonizes the path with paths
      // where assetId === referenceAssetId
      return fullPath.slice(0, referenceIndex + 1);
    },
    /*
     * Load the equipment status for the given asset.
     * The result is cached for 2 minutes. Pass force=true to bypass the TTL check.
     * At most 5 requests are issued in parallel via the global concurrency manager.
     */
    async loadEquipmentStatus(assetId: TAssetId, force?: boolean): Promise<TGetEquipmentStatusResponse> {
      const TTL_MS = 2 * 60 * 1000;

      const cachedAt = this.equipmentStatusTimestampMap[assetId];
      const isCacheValid = cachedAt !== undefined && Date.now() - cachedAt < TTL_MS;

      if (!force && isCacheValid && this.equipmentStatusMap[assetId] !== undefined) {
        return this.equipmentStatusMap[assetId];
      }

      const existingPromise = this.equipmentStatusPromiseMap[assetId];
      if (existingPromise) {
        return existingPromise;
      }

      const promise = globalConcurrencyManager.withLock(
        'asset/equipment-status',
        () => getEquipmentStatus({
          path: {
            assetId,
          },
        }),
      )
        .then((response) => {
          this.equipmentStatusMap[assetId] = response.data;
          this.equipmentStatusTimestampMap[assetId] = Date.now();
          return response.data;
        })
        .finally(() => {
          delete this.equipmentStatusPromiseMap[assetId];
        });

      this.equipmentStatusPromiseMap[assetId] = promise;
      return promise;
    },
    async refetchAssetTree() {
      const allHierarchyKeys = new Set([
        ...Object.keys(this.assetTreeMapWithOfflineAssets),
        ...Object.keys(this.assetTreePromiseMap),
      ]);

      const hierarchyLevels = Array.from(allHierarchyKeys) as unknown as EHierarchy[];

      const promiseList = hierarchyLevels.map((level) => this.loadAssetTree(level, true));

      promiseList.push(this.loadAssetOverview(true));

      await Promise.all(promiseList);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAssetStore, import.meta.hot));
}
